import type { OrderView } from '@khaddi/contracts';
import type { ReactNode } from 'react';
import { Icon, Text, colors, formatVnd, radii, type IconName } from '@khaddi/ui';
import { Linking, Pressable, View } from 'react-native';
import { initialsOf, paymentName } from '../format';
import { PhotoInput } from './PhotoInput';

export const StatusPill = ({ label, tone }: { label: string; tone: 'positive' | 'warning' | 'info' }) => {
  const palette = { positive: [colors.greenSoft, colors.green], warning: [colors.goldSoft, colors.goldText], info: [colors.surfaceMuted, colors.primaryDark] }[tone];
  return (
    <View style={{ backgroundColor: palette[0], borderRadius: radii.pill, paddingHorizontal: 10, paddingVertical: 4 }}>
      <Text variant="label" color={palette[1]} style={{ fontSize: 12, fontWeight: '700' }}>
        {label}
      </Text>
    </View>
  );
};

const CircleButton = ({ icon, label, onPress }: { icon: IconName; label: string; onPress: () => void }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    onPress={onPress}
    style={({ pressed }) => ({ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center', transform: [{ scale: pressed ? 0.94 : 1 }] })}
  >
    <Icon name={icon} size={20} color={colors.onPrimary} />
  </Pressable>
);

const PartyCard = ({ badge, badgeColor, title, subtitle, action }: { badge: ReactNode; badgeColor: string; title: string; subtitle: string; action?: ReactNode }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: colors.background, borderRadius: 16, padding: 12 }}>
    <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: badgeColor, alignItems: 'center', justifyContent: 'center' }}>{badge}</View>
    <View style={{ flex: 1, minWidth: 0 }}>
      <Text variant="bodyStrong">{title}</Text>
      <Text variant="caption">{subtitle}</Text>
    </View>
    {action}
  </View>
);

export const CustomerCard = ({ order }: { order: OrderView }) => {
  const delivery = order.delivery;
  const name = delivery?.recipientName ?? order.customer?.name ?? 'Khách hàng';
  const phone = delivery?.phone ?? order.customer?.phone ?? null;
  const subtitle = [delivery?.address, delivery?.note ? `Ghi chú: ${delivery.note}` : null].filter(Boolean).join(' · ');
  return (
    <PartyCard
      badgeColor={colors.gold}
      badge={
        <Text variant="bodyStrong" style={{ fontWeight: '800' }}>
          {initialsOf(name)}
        </Text>
      }
      title={name}
      subtitle={subtitle}
      action={phone ? <CircleButton icon="phone" label={`Gọi khách ${name}`} onPress={() => void Linking.openURL(`tel:${phone}`)} /> : null}
    />
  );
};

export const BranchCard = ({ order }: { order: OrderView }) => (
  <PartyCard badgeColor={colors.navy} badge={<Icon name="store" size={22} color={colors.gold} strokeWidth={2.2} />} title={order.branch.name} subtitle={order.branch.address} />
);

export const ItemsList = ({ order }: { order: OrderView }) => (
  <View accessibilityRole="list" style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 16, paddingVertical: 4, paddingHorizontal: 14 }}>
    {order.lines.map((line, index) => (
      <View key={`${line.productId}-${index}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44, borderBottomWidth: 1, borderBottomColor: colors.divider, paddingVertical: 6 }}>
        <Icon name="check" size={20} color={colors.green} strokeWidth={2.4} />
        <Text variant="body" style={{ flex: 1, fontSize: 13 }}>
          <Text variant="bodyStrong" style={{ fontSize: 13 }}>
            {line.quantity}×
          </Text>{' '}
          {line.productName}{' '}
          <Text variant="body" color={colors.textMuted} style={{ fontSize: 13 }}>
            · {line.optionsLabel}
          </Text>
        </Text>
      </View>
    ))}
    <View style={{ minHeight: 40, justifyContent: 'center' }}>
      <Text variant="caption">Kiểm tra tem niêm phong còn nguyên trước khi giao</Text>
    </View>
  </View>
);

export const CollectBox = ({ order }: { order: OrderView }) => {
  const cod = order.paymentMethod === 'cod';
  return (
    <View style={{ flex: 1, backgroundColor: cod ? colors.goldSoft : colors.greenSoft, borderRadius: radii.md, paddingVertical: 10, paddingHorizontal: 12, justifyContent: 'center' }}>
      <Text variant="label" color={cod ? colors.goldText : colors.green} style={{ fontSize: 11 }}>
        Thu của khách
      </Text>
      <Text variant="bodyStrong" color={cod ? colors.goldText : colors.greenText} style={{ fontSize: 18, fontWeight: '800' }} numeric>
        {cod ? `${formatVnd(order.pricing.total)} · Tiền mặt` : `0đ · Đã trả ${paymentName(order)}`}
      </Text>
    </View>
  );
};

export const PhotoTile = ({ photo, onPicked }: { photo: string | null; onPicked: (reference: string) => void }) => (
  <PhotoInput label={photo ? 'Chụp lại ảnh giao hàng' : 'Chụp ảnh giao hàng'} onPicked={onPicked} style={{ width: 108 }}>
    <View
      style={{
        flex: 1,
        minHeight: 64,
        borderRadius: radii.md,
        borderWidth: 1.5,
        borderStyle: 'dashed',
        borderColor: photo ? colors.green : colors.sky,
        backgroundColor: photo ? colors.greenSoft : colors.background,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 6,
      }}
    >
      <Icon name={photo ? 'check' : 'camera'} size={20} color={photo ? colors.green : colors.primary} strokeWidth={photo ? 2.6 : 2} />
      <Text variant="label" color={colors.text} style={{ fontSize: 12, textAlign: 'center' }} numberOfLines={1}>
        {photo ? 'Đã chụp ảnh' : 'Ảnh giao hàng'}
      </Text>
    </View>
  </PhotoInput>
);
